import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Brain, MapPin, Briefcase, ChartBar, ArrowSquareOut } from "@phosphor-icons/react";
import VerifiedBadge from "../components/jobsiq/VerifiedBadge";

export default function PublicProfile() {
    const { userId } = useParams();
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/profile/${userId}`)
            .then(({ data }) => setProfile(data))
            .catch(() => setError("Profile not found"));
    }, [userId]);

    if (error)
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="public-profile-not-found">
                {error}
            </div>
        );
    if (!profile)
        return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

    return (
        <div className="min-h-screen bg-white" data-testid="public-profile-page">
            <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
                <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-950 flex items-center justify-center">
                            <Brain size={20} color="#fff" weight="duotone" />
                        </div>
                        <span className="font-display text-xl">
                            Jobs<span className="text-blue-600">IQ</span>
                        </span>
                    </Link>
                    <Link
                        to="/register?role=company"
                        className="text-sm bg-slate-950 text-white px-4 py-2 hover:bg-blue-600"
                    >
                        Hire on Jobs IQ
                    </Link>
                </div>
            </header>

            <section className="bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="relative max-w-[1200px] mx-auto px-6 py-16 grid md:grid-cols-[auto_1fr] gap-8 items-center">
                    <div className="w-32 h-32 bg-white text-slate-950 flex items-center justify-center font-display text-5xl border border-white/20">
                        {(profile.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-400">
                            Public profile · Jobs IQ
                        </div>
                        <h1 className="font-display text-5xl mt-2 leading-tight">{profile.name}</h1>
                        {profile.is_verified && (
                            <div className="mt-3">
                                <VerifiedBadge size="lg" tone="dark" testId="public-profile-verified" />
                            </div>
                        )}
                        <div className="text-slate-300 mt-2 flex flex-wrap gap-4 text-sm">
                            {profile.title && (
                                <span className="inline-flex items-center gap-1.5">
                                    <Briefcase size={14} /> {profile.title}
                                </span>
                            )}
                            {profile.location && (
                                <span className="inline-flex items-center gap-1.5">
                                    <MapPin size={14} /> {profile.location}
                                </span>
                            )}
                            {profile.experience_years ? (
                                <span className="font-mono-plex">{profile.experience_years}+ years</span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-6 py-10 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    {profile.bio && (
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                About
                            </div>
                            <p className="text-slate-800 leading-relaxed whitespace-pre-line">{profile.bio}</p>
                        </div>
                    )}
                    {profile.skills?.length > 0 && (
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-3">
                                Skills
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {profile.skills.map((s) => (
                                    <span key={s} className="text-xs px-2 py-1 border border-slate-300 font-mono-plex">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="space-y-6">
                    <div className="border border-slate-200 p-6">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                            <ChartBar size={14} /> Activity
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div>
                                <div className="font-display text-3xl">{profile.stats?.applications ?? 0}</div>
                                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">
                                    Applications
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-3xl">{profile.stats?.avg_match ?? 0}</div>
                                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">
                                    Avg. AI match
                                </div>
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/register?role=company"
                        data-testid="hire-on-jobsiq-cta"
                        className="block border border-slate-200 p-6 hover:border-slate-950"
                    >
                        <div className="font-display text-xl">Hire {profile.name?.split(" ")[0]}'s peers.</div>
                        <p className="text-sm text-slate-600 mt-1">
                            Post a role on Jobs IQ and meet AI-ranked candidates by tomorrow.
                        </p>
                        <div className="mt-3 inline-flex items-center gap-1 text-blue-600 text-sm">
                            Get started <ArrowSquareOut size={14} />
                        </div>
                    </Link>
                </div>
            </section>
        </div>
    );
}
